import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/site/PageHeader";
import { SiteFooter } from "@/components/site/FinalCTA";
import AuthDialog from "@/components/site/AuthDialog";
import { useAuth } from "@/context/AuthContext";
import { cardsApi, portraitsApi } from "@/lib/cardsApi";
import { formatApiError } from "@/lib/api";
import { MY_CARDS } from "@/constants/testIds";

function errMsg(e) {
  return formatApiError(e?.response?.data?.detail) || e?.message || "Something went wrong.";
}

export default function MyCardsPage() {
  const { user, ready } = useAuth();
  const [items, setItems] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await cardsApi.occasions();
        setOccasions(d.occasions);
      } catch (e) { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    if (!ready || !user) return;
    (async () => {
      try {
        const d = await cardsApi.listMine();
        setItems(d.cards);
      } catch (e) { toast.error(errMsg(e)); }
    })();
  }, [ready, user]);

  async function togglePublish(card, occasion) {
    try {
      const updated = await cardsApi.patchMine(card.id, { publish_to: occasion });
      setItems((cur) => cur.map((c) => (c.id === card.id ? updated : c)));
      toast.success(occasion ? "Published." : "Unpublished.");
    } catch (e) { toast.error(errMsg(e)); }
  }

  async function remove(card) {
    if (!window.confirm(`Delete this card? This can't be undone.`)) return;
    try {
      await cardsApi.removeMine(card.id);
      setItems((cur) => cur.filter((c) => c.id !== card.id));
      toast.success("Deleted.");
    } catch (e) { toast.error(errMsg(e)); }
  }

  if (!ready) return <div className="min-h-screen bg-ivory" />;

  if (!user) {
    return (
      <div data-testid={MY_CARDS.page} className="min-h-screen bg-ivory text-ink">
        <PageHeader
          eyebrow="My Cards"
          title="Every card you've"
          italic="ever"
          tail="made — kept safe."
          subtitle="Sign in to see your gallery. Publish any card to a public feed with one click, or keep it private."
        />
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-20">
          <div className="rounded-2xl border border-ivory-300 bg-white/60 p-10 text-center">
            <p className="font-serif text-2xl text-ink">Sign in to see your cards.</p>
            <button
              onClick={() => setAuthOpen(true)}
              className="mt-8 rounded-full bg-saffron text-ivory px-6 py-3 font-medium hover:bg-saffron-deep transition"
            >
              Sign in or register
            </button>
          </div>
        </div>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialTab="register" />
        <SiteFooter />
      </div>
    );
  }

  return (
    <div data-testid={MY_CARDS.page} className="min-h-screen bg-ivory text-ink">
      <PageHeader
        eyebrow="My Cards"
        title="Every card you've"
        italic="ever"
        tail="made — kept safe."
        subtitle="Publish any card to a public feed with one click, or keep it private forever. You can always change your mind."
      />

      <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-10 pb-24">
        <div className="flex flex-wrap items-center gap-3 border-b border-ivory-300 pb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-50">
            {items.length} {items.length === 1 ? "card" : "cards"}
          </div>
          <Link
            to="/cards"
            className="ml-auto h-10 rounded-full bg-saffron text-ivory px-5 text-sm font-medium hover:bg-saffron-deep transition inline-flex items-center"
          >
            + Create a card
          </Link>
        </div>

        {items.length === 0 ? (
          <div className="mt-16 rounded-2xl border border-ivory-300 bg-white/60 p-14 text-center">
            <p className="font-serif text-3xl text-ink">No cards yet.</p>
            <p className="mt-2 text-ink-50 max-w-md mx-auto">
              Compose your first personal message card, or upload a card you made elsewhere.
            </p>
            <Link
              to="/cards"
              className="mt-6 inline-block rounded-full bg-saffron text-ivory px-5 py-2.5 text-sm font-medium hover:bg-saffron-deep transition"
            >
              Open the card studio →
            </Link>
          </div>
        ) : (
          <div data-testid={MY_CARDS.grid} className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
            {items.map((c) => (
              <motion.div
                key={c.id}
                data-testid={MY_CARDS.card(c.id)}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="group rounded-2xl border border-ivory-300 bg-white/70 overflow-hidden"
              >
                <div className="aspect-[1024/1536] overflow-hidden bg-ivory-100">
                  <img
                    src={portraitsApi.publicUrl(c.url)}
                    alt={c.title || "Card"}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-saffron">
                    {c.occasion.replace(/_/g, " ")}
                  </div>
                  <div className="mt-1 font-serif text-ink truncate">{c.title || c.person_name || "Untitled"}</div>
                  <div className="mt-2 text-xs text-ink-50">
                    {c.publish_to
                      ? (<>Public in <Link to={`/celebrate/${c.publish_to}`} className="text-tulsi hover:text-saffron">{occasionLabel(occasions, c.publish_to)}</Link></>)
                      : "Private"}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {c.publish_to ? (
                      <button
                        data-testid={MY_CARDS.unpublish(c.id)}
                        onClick={() => togglePublish(c, "")}
                        className="text-xs px-3 h-8 rounded-full border border-ivory-300 hover:border-saffron hover:text-saffron transition"
                      >
                        Unpublish
                      </button>
                    ) : (
                      <button
                        data-testid={MY_CARDS.publish(c.id)}
                        onClick={() => togglePublish(c, c.occasion)}
                        className="text-xs px-3 h-8 rounded-full bg-saffron text-ivory hover:bg-saffron-deep transition"
                      >
                        Publish to {occasionLabel(occasions, c.occasion)}
                      </button>
                    )}
                    <a
                      href={portraitsApi.publicUrl(c.url)}
                      download
                      className="text-xs px-3 h-8 inline-flex items-center rounded-full border border-ivory-300 hover:border-tulsi hover:text-tulsi transition"
                    >
                      Download
                    </a>
                    <button
                      data-testid={MY_CARDS.remove(c.id)}
                      onClick={() => remove(c)}
                      className="ml-auto text-xs px-3 h-8 rounded-full text-rust hover:bg-rust/10 transition"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}

function occasionLabel(list, id) {
  return list.find((o) => o.id === id)?.label || id;
}
